import { INoiseProcessor, AudioQualityMetrics } from './types';

// Public paths for worklets
const WORKLET_PATH_RNNOISE = `${process.env.PUBLIC_URL}/static/js/NoiseSuppressorWorklet.js`;

export class RNNoiseProcessor implements INoiseProcessor {
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private destinationNode: MediaStreamAudioDestinationNode | null = null;

  // Post-processing nodes (Simplified Chain)
  // Removed Compressor and EQ to eliminate "saturation" artifacts.
  // Gate is now handled inside the AudioWorklet for zero-latency.

  private isWorkletLoaded: boolean = false;

  // VAD callbacks
  private metricsCallbacks: Set<(metrics: AudioQualityMetrics) => void> = new Set();
  // private metricsInterval: any = null; // Deprecated: Metrics handled by worklet

  // Enhanced RNNoise configuration for better noise suppression
  private readonly RNNOISE_CONFIG = {
    // Enhanced noise suppression parameters
    BASE_NOISE_FLOOR_GAIN: 0.3, // Gentle baseline suppression (was implicit 1.0)
    MAX_NOISE_ATTENUATION: 0.1, // Maximum noise floor attenuation (was 0.05)
    VAD_SENSITIVITY_BOOST: 1.2, // Boost VAD confidence for better detection
    SPECTRAL_SMOOTHING: 0.85, // Spectral smoothing factor for cleaner transitions
    TRANSIENT_PROTECTION: 0.9, // Protect speech transients from over-suppression

    // Adaptive parameters
    ADAPTIVE_THRESHOLD: 0.35, // Adaptive VAD threshold
    NOISE_LEARN_RATE: 0.02, // Noise profile learning rate
    SPEECH_PRESERVATION: 0.95, // Speech preservation factor
  };

  // Smart Mixing State
  private smartMixingEnabled: boolean = true;
  private isProcessing: boolean = false; // "Processing" here means "Not Muted" (Audio is flowing)
  private isBypassed: boolean = false; // "Bypassed" means effects are skipped (Raw Audio)

  constructor() {}

  async init(context: AudioContext): Promise<void> {
    this.audioContext = context;
    const ctx = this.audioContext;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    if (!this.isWorkletLoaded) {
      try {
        await ctx.audioWorklet.addModule(WORKLET_PATH_RNNOISE);
        this.isWorkletLoaded = true;
      } catch (error) {
        console.error('[RNNoiseProcessor] Failed to load audio worklet module', error);
        throw error;
      }
    }
  }

  async process(stream: MediaStream): Promise<MediaStream> {
    const ctx = this.audioContext;
    if (!ctx) throw new Error('RNNoiseProcessor not initialized with AudioContext');

    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    this.disconnectNodes();

    // 1. Create Worklet Node (The Noise Filter + Zero Latency Gate)
    const processorName = 'NoiseSuppressorWorklet';
    this.workletNode = new AudioWorkletNode(ctx, processorName);

    this.workletNode.port.start();

    // 2. Build Audio Graph (Pure Chain)
    // Source -> Worklet -> Destination
    this.sourceNode = ctx.createMediaStreamSource(stream);
    this.destinationNode = ctx.createMediaStreamDestination();

    // Connections
    // Connections
    this.updateConnections();

    // Apply default params
    this.updateParams();

    this.isProcessing = true;
    console.log('[RNNoiseProcessor] Zero-Latency Chain Active');

    return this.destinationNode.stream;
  }

  // Set params for the Worklet (WASM) - ENHANCED WITH BETTER NOISE SUPPRESSION
  private updateParams() {
    if (!this.workletNode) return;
    this.workletNode.port.postMessage({
      type: 'SET_PARAMS',
      payload: {
        speechMix: 0.0, // 0% raw mix, pure filtered output
        // ENHANCED NOISE SUPPRESSION PARAMETERS
        extraSuppression: true,
        noiseFloorGain: this.RNNOISE_CONFIG.BASE_NOISE_FLOOR_GAIN,
        suppressionThreshold: this.RNNOISE_CONFIG.ADAPTIVE_THRESHOLD,
        // Advanced parameters for better noise handling
        spectralSmoothing: this.RNNOISE_CONFIG.SPECTRAL_SMOOTHING,
        transientProtection: this.RNNOISE_CONFIG.TRANSIENT_PROTECTION,
      },
    });
    // Sync Smart Mixing state
    this.setSmartMixing(this.smartMixingEnabled);
  }

  async setMuted(muted: boolean): Promise<void> {
    if (muted) {
      if (this.sourceNode) {
        try {
          this.sourceNode.disconnect();
        } catch (e) {
          // ignore
        }
      }
      if (this.workletNode) {
        try {
          this.workletNode.disconnect();
        } catch (e) {
          // ignore
        }
      }
      this.isProcessing = false;
    } else {
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      this.isProcessing = true;
      this.updateConnections();
    }
  }

  /*
   * Enables or disables the noise suppression effect.
   * When enabled=false, we bypass the worklet and route Source -> Destination directly.
   */
  async enableProcessing(enabled: boolean): Promise<void> {
    const shouldBypass = !enabled;
    if (this.isBypassed === shouldBypass) return;

    this.isBypassed = shouldBypass;

    // Only update connections if we are currently "flowing" (not muted)
    if (this.isProcessing) {
      this.updateConnections();
    }
  }

  private updateConnections() {
    if (!this.sourceNode || !this.destinationNode || !this.workletNode) return;

    // 1. Disconnect everything to start fresh
    try {
      this.sourceNode.disconnect();
    } catch (e) {}
    try {
      this.workletNode.disconnect();
    } catch (e) {}

    // 2. Route based on Bypass state
    if (this.isBypassed) {
      // RAW PATH: Source -> Destination
      // console.log('[RNNoiseProcessor] Bypass Active (Raw Audio)');
      this.sourceNode.connect(this.destinationNode);
    } else {
      // PROCESSED PATH: Source -> Worklet -> Destination
      // console.log('[RNNoiseProcessor] Processing Active (Filtered Audio)');
      this.sourceNode.connect(this.workletNode);
      this.workletNode.connect(this.destinationNode);
    }
  }

  setSmartMixing(enabled: boolean): void {
    this.smartMixingEnabled = enabled;
    // Tell Worklet to enable/disable Zero-Latency Gate
    if (this.workletNode) {
      this.workletNode.port.postMessage({
        type: 'SET_SMART_MIXING',
        payload: { enabled },
      });
    }
  }

  setSuppressionLevel(level: number): void {
    if (!this.workletNode) return;
    // Map 0-100 to noise floor gain
    // 0 = 1.0 (no extra suppression), 100 = 0.05 (heavy suppression)
    const noiseFloorGain = Math.max(0.05, 1.0 - (level / 100) * 0.95);
    this.workletNode.port.postMessage({
      type: 'SET_PARAMS',
      payload: {
        noiseFloorGain,
        extraSuppression: level > 0,
      },
    });
  }

  stop(): void {
    this.disconnectNodes();
    this.isProcessing = false;
    this.isWorkletLoaded = false;
    // this.stopMetricsLoop();
  }

  destroy(): void {
    this.stop();
    this.metricsCallbacks.clear();
  }

  onMetrics(callback: (metrics: AudioQualityMetrics) => void): void {
    this.metricsCallbacks.add(callback);
  }

  /* Deprecated: Metrics loop removed in favor of Worklet push
  private startSoftGateLoop() { ... }
  private stopMetricsLoop() { ... }
  */

  private disconnectNodes() {
    const nodes = [this.sourceNode, this.workletNode, this.destinationNode];

    nodes.forEach((node) => {
      if (node) {
        try {
          node.disconnect();
        } catch (e) {}
      }
    });

    this.sourceNode = null;
    this.workletNode = null;
    this.destinationNode = null;
  }

  get node(): AudioWorkletNode | null {
    return this.workletNode;
  }
}
